import { UserPlus01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LoaderFunction } from 'react-router-dom';
import { Link, Navigate, useLoaderData } from 'react-router-dom';

import { validateEmail } from '@/lib/auth/constants';
import { getProviderButtonLabel } from '@/lib/auth/provider-labels';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { HintText } from '@/lib/base/forms';
import { FormProvider } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import { Input } from '@/lib/base/forms/input/input';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import { humanize } from '@/utils/text';

import FormErrors from '../../components/FormErrors';
import { useOrg } from '../../contexts/OrgContext';
import AuthLayout from '../../layouts/AuthLayout.tsx';
import { AuthProcess, signUp } from '../../lib/allauth';
import ProviderList from '../auth/ProviderList.tsx';

interface InvitationData {
  id: string;
  email: string;
  role: string;
  is_accepted: boolean;
  team_name: string;
  invited_by_name: string;
  org_id: number;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  member: 'Member',
};

type LoaderData =
  | { invitation: InvitationData; invitationId: string }
  | { invitation: null; invitationId: string };

// eslint-disable-next-line react-refresh/only-export-components -- route loader co-located with page
export const loader: LoaderFunction = async ({
  params,
}): Promise<LoaderData> => {
  const { invitationId } = params;
  if (!invitationId) {
    throw new Error('Invitation ID is required');
  }

  try {
    const response = await fetchWithDefaults(
      `/api/invitations/${invitationId}/`
    );

    if (!response.ok) {
      if (response.status === 404) {
        return { invitation: null, invitationId };
      }
      throw new Error(`Failed to fetch invitation: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      invitation: data,
      invitationId,
    };
  } catch (error) {
    logger.error(
      'Error fetching invitation',
      error instanceof Error ? error : new Error(String(error)),
      { component: 'teams', location: 'AcceptInvitation' }
    );
    throw error;
  }
};

function AcceptInvitationContent({
  invitation,
  invitationId,
}: {
  invitation: InvitationData;
  invitationId: string;
}) {
  const { refreshCurrentOrg } = useOrg();
  const [response, setResponse] = useState<{
    fetching: boolean;
    success: boolean;
    error: string | null;
    orgId: number | null;
    /**
     * True when the accepted membership still owes the join wizard. Decides whether we
     * land the invitee on the wizard or the dashboard — see the redirect below.
     */
    joinOnboardingPending: boolean;
  }>({
    fetching: false,
    success: false,
    error: null,
    orgId: null,
    joinOnboardingPending: false,
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [signupServerErrors, setSignupServerErrors] = useState<
    Array<{ message: string; param: string }> | undefined
  >(undefined);

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await fetchWithDefaults(
        '/_allauth/browser/v1/auth/session'
      );
      const data = await response.json();
      setIsAuthenticated(data.status === 200 && data.data?.user);
    } catch (error) {
      logger.error(
        'Error checking auth status',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'teams', location: 'AcceptInvitation' }
      );
      setIsAuthenticated(false);
    }
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const acceptInvitation = useCallback(async () => {
    logger.debug('[AcceptInvitation] Button clicked, starting accept flow');
    setResponse(prev => ({ ...prev, fetching: true, error: null }));

    try {
      const url = `/api/invitations/${invitationId}/`;
      logger.debug('[AcceptInvitation] Making POST request', { url });

      const resp = await fetchWithDefaults(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      logger.debug('[AcceptInvitation] Response status', {
        status: resp.status,
        statusText: resp.statusText,
      });

      if (!resp.ok) {
        // Try to parse error from JSON response, fallback to status text
        let errorMessage = "We couldn't accept the invitation.";
        try {
          const data = await resp.json();
          errorMessage = data.error || errorMessage;
        } catch {
          errorMessage = `${errorMessage} (${resp.status} ${resp.statusText})`;
        }
        logger.error('Error response', new Error(errorMessage), {
          component: 'teams',
          location: 'AcceptInvitation',
        });
        throw new Error(errorMessage);
      }

      const data = await resp.json();
      logger.info('[AcceptInvitation] Successfully accepted invitation', {
        orgId: data.org_id,
      });

      // Validate that org_id was returned
      if (!data.org_id) {
        logger.error(
          'API response missing org_id',
          new Error('Invalid response from server: missing org_id'),
          { component: 'teams', location: 'AcceptInvitation', data }
        );
        throw new Error('Invalid response from server: missing org_id');
      }

      // Refresh org list to include the newly joined org
      logger.debug('[AcceptInvitation] Refreshing org list...');
      await refreshCurrentOrg();

      // Set success response — the redirect below sends the invitee to the join
      // wizard when they still owe it, otherwise to /${org_id} where TeamGuard
      // switches to the org from the URL.
      setResponse({
        fetching: false,
        success: true,
        error: null,
        orgId: data.org_id,
        joinOnboardingPending: Boolean(data.join_onboarding_pending),
      });
    } catch (error: unknown) {
      const errorMessage = 'An error occurred while accepting the invitation';
      logger.error(
        'Error accepting invitation',
        error instanceof Error ? error : new Error(errorMessage),
        { component: 'teams', location: 'AcceptInvitation' }
      );
      setResponse({
        fetching: false,
        success: false,
        error: errorMessage,
        orgId: null,
        joinOnboardingPending: false,
      });
    }
  }, [invitationId, refreshCurrentOrg]);

  // Bind the invitation to the session before allauth redirects to the chosen
  // provider, so the backend adapter can complete signup against this
  // invitation. Throws on failure so ProviderList aborts the redirect and
  // surfaces the message via onOAuthError. Provider-agnostic – the prepare step
  // is identical for Google, Microsoft, or any future provider.
  const prepareOAuth = useCallback(async () => {
    const url = `/api/invitations/${invitationId}/`;
    logger.debug('[AcceptInvitation] Preparing OAuth, calling PUT', { url });

    const resp = await fetchWithDefaults(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    logger.debug('[AcceptInvitation] Prepare OAuth response', {
      status: resp.status,
    });

    if (!resp.ok) {
      let errorMessage = "We couldn't start sign-in.";
      try {
        const data = await resp.json();
        errorMessage = data.error || errorMessage;
      } catch {
        errorMessage = `${errorMessage} (${resp.status} ${resp.statusText})`;
      }
      throw new Error(errorMessage);
    }

    logger.debug(
      '[AcceptInvitation] Session prepared, redirecting to provider'
    );
  }, [invitationId]);

  const handleOAuthError = useCallback((message: string) => {
    logger.error('Error preparing OAuth', new Error(message), {
      component: 'teams',
      location: 'AcceptInvitation',
    });
    setResponse(prev => ({
      ...prev,
      fetching: false,
      success: false,
      error: message,
      orgId: null,
    }));
  }, []);

  const formFields = useMemo(
    () => ({
      email: { required: true, validate: (v: unknown) => validateEmail(v) },
      password: { required: true },
      confirmPassword: {
        required: true,
        validate: (v: unknown) =>
          v === password ? undefined : 'Passwords must match',
      },
    }),
    [password]
  );

  const formValues = useMemo(
    () => ({ email: invitation.email, password, confirmPassword }),
    [invitation.email, password, confirmPassword]
  );

  const handlePasswordSignup = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (password !== confirmPassword) {
        setSignupError('Passwords must match');
        return;
      }
      setSignupSubmitting(true);
      setSignupError(null);
      setSignupServerErrors(undefined);
      try {
        const prepareResp = await fetchWithDefaults(
          `/api/invitations/${invitationId}/`,
          { method: 'PUT', headers: { 'Content-Type': 'application/json' } }
        );
        if (!prepareResp.ok) {
          let msg = "We couldn't start your signup.";
          try {
            const data = await prepareResp.json();
            msg = data.error || msg;
          } catch {
            // fall through with default
          }
          throw new Error(msg);
        }
        const signupResp = await signUp({
          email: invitation.email,
          password,
        });
        if (signupResp.status >= 400 && signupResp.errors) {
          setSignupServerErrors(signupResp.errors);
          throw new Error(
            signupResp.errors[0]?.message || 'Signup request failed'
          );
        }
        if (signupResp.status >= 400) {
          throw new Error('Signup request failed');
        }
        await refreshCurrentOrg();
        setResponse({
          fetching: false,
          success: true,
          error: null,
          orgId: invitation.org_id,
          // The password-signup path never calls the accept endpoint — allauth's
          // adapter accepts the invitation server-side during signup — so there is
          // no accept response to read the flag from. Fall back to the dashboard and
          // let JoinOnboardingRedirect route them; that path keeps the brief flash.
          joinOnboardingPending: false,
        });
      } catch (err: unknown) {
        logger.error(
          'Error during invitation password signup',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'teams', location: 'AcceptInvitation' }
        );
        setSignupError("We couldn't sign you up. Try again.");
        setSignupSubmitting(false);
      }
    },
    [
      invitationId,
      invitation.email,
      invitation.org_id,
      password,
      confirmPassword,
      refreshCurrentOrg,
    ]
  );

  // Redirect after a successful accept.
  //
  // An invitee who still owes the join wizard goes STRAIGHT there. Sending them to
  // /{orgId} first paints the dashboard — a workspace they have not finished joining —
  // before JoinOnboardingRedirect bounces them into the wizard. The allauth adapter
  // cannot prevent this: the SPA uses allauth headless, so its redirect hooks
  // (get_signup_redirect_url / get_login_redirect_url) never run for this flow.
  if (response.success && response.orgId) {
    const target = response.joinOnboardingPending
      ? `/join/${response.orgId}`
      : `/${response.orgId}`;
    return <Navigate to={target} />;
  }

  // Already accepted
  if (invitation.is_accepted) {
    return (
      <AuthLayout title='Invitation already accepted'>
        <div className='flex flex-col gap-8'>
          {/* Header */}
          <div className='flex flex-col gap-3'>
            <h1 className='font-display text-4xl font-medium text-brand-600 dark:text-brand-400'>
              Already accepted
            </h1>
            <p className='text-base text-[var(--color-text-tertiary)]'>
              This invitation has already been accepted.
            </p>
          </div>

          {/* Action */}
          <Button href='/' className='w-full'>
            Go to dashboard
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Show loading while checking auth status
  if (isAuthenticated === null) {
    return (
      <AuthLayout title='Accept team invitation'>
        <div className='flex flex-col gap-8'>
          <h1 className='font-display text-4xl font-medium text-brand-600 dark:text-brand-400'>
            Accept team invitation
          </h1>
          <div className='flex items-center justify-center py-12'>
            <LoadingIndicator size='sm' />
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title='Accept team invitation'>
      <div className='flex flex-col gap-8'>
        {/* Header */}
        <div className='flex flex-col gap-3'>
          <h1 className='font-display text-4xl font-medium text-brand-600 dark:text-brand-400'>
            Come aboard!
          </h1>
          <p className='text-base text-[var(--color-text-tertiary)]'>
            You have been invited to use Ahoy, the AI-native CRM – sign in and
            get to selling.
          </p>
        </div>

        {/* Invitation Details */}
        <div className='flex flex-col gap-6'>
          <div className='rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-4'>
            <div className='flex flex-col gap-3'>
              <div className='flex items-start justify-between'>
                <div className='flex flex-col gap-1'>
                  <span className='text-sm font-medium text-[var(--color-text-secondary)]'>
                    Email address
                  </span>
                  <span className='text-sm text-[var(--color-text-primary)]'>
                    {invitation.email}
                  </span>
                </div>
              </div>
              <div className='border-t border-[var(--color-border-secondary)] pt-3'>
                <div className='flex flex-col gap-1'>
                  <span className='text-sm font-medium text-[var(--color-text-secondary)]'>
                    Role
                  </span>
                  <Badge color='brand' size='sm'>
                    {ROLE_LABELS[invitation.role] ?? humanize(invitation.role)}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {response.error && (
            <div role='alert'>
              <HintText isInvalid showError>
                {response.error}
              </HintText>
            </div>
          )}

          {/* Authenticated: Show Accept Button */}
          {isAuthenticated && (
            <Button
              onClick={acceptInvitation}
              isLoading={response.fetching}
              showTextWhileLoading={true}
              className='w-full'
              iconLeading={
                !response.fetching ? (
                  <UserPlus01 className='h-5 w-5' />
                ) : undefined
              }
            >
              {response.fetching ? 'Accepting…' : 'Accept invitation'}
            </Button>
          )}

          {/* Not Authenticated: Show OAuth provider buttons (Google, Microsoft, …) */}
          {!isAuthenticated && (
            <>
              {/* Shared provider list – same buttons, icons, and preview-gating
                  as the login/signup pages. beforeRedirect binds the invitation
                  to the session before allauth hands off to the provider.
                  callbackURL '/' lets the backend adapter route to the org
                  dashboard after signup. */}
              <ProviderList
                callbackURL='/'
                process={AuthProcess.LOGIN}
                getLabel={getProviderButtonLabel}
                beforeRedirect={prepareOAuth}
                onError={handleOAuthError}
              />

              {/* Divider */}
              <div className='flex items-center gap-3'>
                <div className='h-px flex-1 bg-[var(--color-border-secondary)]' />
                <span className='text-sm text-[var(--color-text-tertiary)]'>
                  or
                </span>
                <div className='h-px flex-1 bg-[var(--color-border-secondary)]' />
              </div>

              {/* Password signup form */}
              <FormProvider fields={formFields} values={formValues}>
                <form
                  onSubmit={handlePasswordSignup}
                  noValidate
                  className='flex flex-col gap-4'
                >
                  <p className='text-sm text-[var(--color-text-tertiary)]'>
                    Or set a password instead.
                  </p>
                  <FormField name='email' label='Email' isRequired>
                    {({ inputId, labelId }) => (
                      <Input
                        id={inputId}
                        aria-labelledby={labelId}
                        type='email'
                        className='w-full'
                        value={invitation.email}
                        isDisabled
                        name='email'
                      />
                    )}
                  </FormField>
                  <FormField name='password' label='Password' isRequired>
                    {({ inputId, labelId, isInvalid, showError }) => (
                      <Input
                        id={inputId}
                        aria-labelledby={labelId}
                        type='password'
                        className='w-full'
                        placeholder='Create a password'
                        isRequired
                        value={password}
                        onChange={setPassword}
                        isInvalid={isInvalid}
                        showError={showError}
                        name='password'
                        autoComplete='new-password'
                      />
                    )}
                  </FormField>
                  <FormErrors param='password' errors={signupServerErrors} />
                  <FormField
                    name='confirmPassword'
                    label='Confirm password'
                    isRequired
                  >
                    {({ inputId, labelId, isInvalid, showError }) => (
                      <Input
                        id={inputId}
                        aria-labelledby={labelId}
                        type='password'
                        className='w-full'
                        placeholder='Confirm your password'
                        isRequired
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                        isInvalid={isInvalid}
                        showError={showError}
                        name='confirmPassword'
                        autoComplete='new-password'
                      />
                    )}
                  </FormField>
                  {signupError && (
                    <div role='alert'>
                      <HintText isInvalid showError>
                        {signupError}
                      </HintText>
                    </div>
                  )}
                  <Button
                    type='submit'
                    isLoading={signupSubmitting}
                    showTextWhileLoading
                    className='w-full'
                  >
                    {signupSubmitting ? 'Creating account…' : 'Create account'}
                  </Button>
                </form>
              </FormProvider>

              {/* Traditional Login/Signup Links */}
              <div className='flex flex-col gap-1 text-center'>
                <div className='flex justify-center gap-1'>
                  <Link
                    to={`/account/login?next=/invitation/${invitationId}`}
                    className='text-sm font-semibold text-brand-700 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 transition-colors'
                  >
                    Log in
                  </Link>
                  <span className='text-sm text-[var(--color-text-tertiary)]'>
                    or
                  </span>
                  <Link
                    to={`/account/signup?next=/invitation/${invitationId}`}
                    className='text-sm font-semibold text-brand-700 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 transition-colors'
                  >
                    sign up
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}

export default function AcceptInvitation() {
  const data = useLoaderData() as LoaderData;

  if (data.invitation === null) {
    return (
      <AuthLayout title='Invitation unavailable'>
        <div role='alert' className='flex flex-col gap-8'>
          <div className='flex flex-col gap-3'>
            <h1 className='font-display text-4xl font-medium text-brand-600 dark:text-brand-400'>
              Invitation unavailable
            </h1>
            <p className='text-base text-[var(--color-text-tertiary)]'>
              This invitation may have been revoked or has already been used.
              Reach out to whoever invited you for a new link.
            </p>
          </div>
          <Button href='/' className='w-full'>
            Go to Ahoy
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AcceptInvitationContent
      invitation={data.invitation}
      invitationId={data.invitationId}
    />
  );
}
